// Updated BillHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { parseISO, format } from "date-fns";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Card,
  Table,
  Tabs,
  DatePicker,
  Select,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Popconfirm,
} from "antd";
import {
  HomeOutlined,
  ArrowLeftOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";
import logo from "../../assets/logo.png";
import BillItemModal from "../../components/Modals/BillItemModal";
import { getUserType } from "../../utility/auth";
import cancelBill from "../../functions/cancelBill";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const PAYMENT_TAG_COLOR = {
  Cash: "green",
  Credit: "orange",
  "Bank Transfer": "blue",
  Entertainment: "magenta",
  UPI: "purple",
  "QR Code": "cyan",
};

export default function BillHistory() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [orderNoSearch, setOrderNoSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [tableFilter, setTableFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState({ min: "", max: "" });
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [customers, setCustomers] = useState([]);

  const navigate = useNavigate();
  const userType = getUserType();
  const isCashier = userType?.toLowerCase() === 'cashier';

  const customerNameById = useMemo(() => {
    const map = {};
    customers.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [customers]);

  useEffect(() => {
    fetchData("customers", setCustomers, "id", {}).catch((error) => {
      console.error("Error fetching customers:", error);
    });
  }, []);

  const fetchAllData = async () => {
    try {
      await fetchData("final_bill", setData, "id", {});
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleCancelBill = async (row) => {
    try {
      await cancelBill("final_bill", "id", row.id);
      toast.success(`Bill #${row.id} cancelled successfully`);
      await fetchAllData();
    } catch (error) {
      console.error("Error cancelling bill:", error);
      toast.error("Failed to cancel bill");
    }
  };

  // Initial data load effect
  useEffect(() => {
    if (data.length > 0) {
      applyFilters();
    }
  }, [data]);

  // Filter change effect
  useEffect(() => {
    if (data.length > 0) {
      applyFilters();
    }
  }, [startDate, endDate, paymentMode, activeTab, orderNoSearch, tableFilter, amountFilter, sortBy, sortOrder]);

  const applyFilters = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error("Invalid date range: Start Date is after End Date");
      return;
    }

    let filtered = [...data];

    // Filter by status (active/cancelled)
    if (activeTab === "cancelled") {
      filtered = filtered.filter(item => item.status === 2 || item.status === "2");
    } else {
      filtered = filtered.filter(item => item.status !== 2 && item.status !== "2");
    }

    // Filter by date range (fall back to inv_date when setup_date wasn't recorded)
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include the entire end date

      filtered = filtered.filter(item => {
        const rawDate = item.setup_date || item.inv_date;
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        return itemDate >= start && itemDate <= end;
      });
    } else if (startDate) {
      const start = new Date(startDate);
      filtered = filtered.filter(item => {
        const rawDate = item.setup_date || item.inv_date;
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        return itemDate >= start;
      });
    } else if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => {
        const rawDate = item.setup_date || item.inv_date;
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        return itemDate <= end;
      });
    }

    // Filter by payment mode
    if (paymentMode) {
      filtered = filtered.filter(item => item.payment_mode === paymentMode);
    }

    // Filter by invoice number
    if (orderNoSearch) {
      filtered = filtered.filter(item =>
        item.id.toString().includes(orderNoSearch.toLowerCase())
      );
    }

    // Filter by table
    if (tableFilter) {
      filtered = filtered.filter(item =>
        item.table_number && item.table_number.toLowerCase().includes(tableFilter.toLowerCase())
      );
    }

    // Filter by amount range
    if (amountFilter.min || amountFilter.max) {
      filtered = filtered.filter(item => {
        const amount = parseFloat(item.grand_total || 0);
        const minAmount = parseFloat(amountFilter.min || 0);
        const maxAmount = parseFloat(amountFilter.max || Infinity);
        return amount >= minAmount && amount <= maxAmount;
      });
    }

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "date":
          aValue = new Date((a.setup_date || a.inv_date) + " " + (a.inv_time || "00:00:00"));
          bValue = new Date((b.setup_date || b.inv_date) + " " + (b.inv_time || "00:00:00"));
          break;
        case "amount":
          aValue = parseFloat(a.grand_total || 0);
          bValue = parseFloat(b.grand_total || 0);
          break;
        case "table":
          aValue = a.table_number || "";
          bValue = b.table_number || "";
          break;
        case "invoice":
          aValue = parseInt(a.id || 0);
          bValue = parseInt(b.id || 0);
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setPaymentMode("");
    setOrderNoSearch("");
    setTableFilter("");
    setAmountFilter({ min: "", max: "" });
    setSortBy("date");
    setSortOrder("desc");
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    localStorage.removeItem("paymentMode");
  };

  const exportCSV = () => {
    const rows = filteredData.length > 0 ? filteredData : data;
    if (rows.length === 0) {
      toast.error("No data to export");
      return;
    }
    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.join(","),
      ...rows.map(row =>
        headers.map(h => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(",")
      ),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "salereport.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.addImage(logo, "PNG", 150, 10, 40, 15);
    doc.setFontSize(16);
    doc.text("Bill History", 14, 20);

    const tableColumn = ["Invoice No", "Date", "Time", "Subtotal", "Grand Total", "Payment Mode"];
    const tableRows = [];

    let totalSubtotal = 0;
    let totalGrandTotal = 0;

    filteredData.forEach(item => {
      const subtotal = parseFloat(item.subtotal_afterdiscount) || 0;
      const grandTotal = parseFloat(item.grand_total) || 0;

      totalSubtotal += subtotal;
      totalGrandTotal += grandTotal;

      tableRows.push([
        item.id,
        item.setup_date,
        item.inv_time,
        subtotal.toFixed(2),
        grandTotal.toFixed(2),
        item.payment_mode,
      ]);
    });

    // Add total row
    tableRows.push([
      { content: "Total", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
      totalSubtotal.toFixed(2),
      totalGrandTotal.toFixed(2),
      "" // Empty payment mode column
    ]);

    doc.autoTable({
      startY: 30,
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 10 },
    });

    doc.save("salereport.pdf");
  };

  const handleViewItems = (row) => {
    setSelectedBill(row);
    setShowModal(true);
  };

  // New function to print VAT invoice with full format from POS
  const printBillVATFormat = async (billData) => {
    try {
      // Fetch order items for the bill
      const itemsResponse = await fetchData("order_items", () => {}, "order_id", { order_id: billData.id });

      // Fetch customer details using customer_id (final_bill only stores customer_id, not customer fields)
      let customerDetails = {
        name: 'Walk-in Customer',
        contact: 'N/A',
        email: '',
        address: '',
        taxid: '',
      };
      if (billData.customer_id) {
        try {
          const customerResponse = await fetchData("customers", () => {}, "id", { id: billData.customer_id });
          if (customerResponse && customerResponse.length > 0) {
            customerDetails = customerResponse[0];
          }
        } catch (error) {
          console.log("Could not fetch customer details", error);
        }
      }

      // Fetch company profile if available
      let companyProfile = {};
      try {
        const companyResponse = await fetchData("company_profile", () => {}, "id", {});
        if (companyResponse && companyResponse.length > 0) {
          companyProfile = companyResponse[0];
        }
      } catch (error) {
        console.log("Using default company info", error);
      }

      // Fetch core settings to check showVat setting
      let showVatDetails = false;
      try {
        const coreSettingResponse = await fetchData("coresetting", () => {}, "id", {});
        if (coreSettingResponse && coreSettingResponse.length > 0) {
          showVatDetails = coreSettingResponse[0].showVat?.toLowerCase() === 'yes';
        }
      } catch (error) {
        console.log("Could not fetch core settings, defaulting showVat to false", error);
      }

      const invoiceData = {
        invoice_number: billData.id,
        setup_date: billData.setup_date,
        inv_time: billData.inv_time,
        payment_method: billData.payment_mode,
        customer_name: customerDetails.name || 'Walk-in Customer',
        customer_phone: customerDetails.contact || 'N/A',
        customer_email: customerDetails.email || '',
        customer_address: customerDetails.address || '',
        customer_gst: customerDetails.taxid || '',
        items: itemsResponse.map(item => ({
          item_name: item.item_name,
          description: item.description || '',
          rate: item.rate || 0,
          quantity: item.quantity,
          discount_percent: item.discount_percent || 0,
          vat: item.vat || 7,
          total_amount: item.total_price,
          offerprice: item.rate || 0,
          iname: item.item_name
        })),
        subtotal: billData.subtotal,
        discount_value: billData.discount_value || 0,
        tax: billData.tax,
        round_off: billData.round_off || 0,
        grand_total: billData.grand_total,
        total: billData.subtotal
      };

      // Helper function to convert blob to base64
      const blobToBase64 = (blob, type) => {
        if (!blob) return null;
        const uint8Array = new Uint8Array(blob.data || blob);
        const binary = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        return `data:${type};base64,${btoa(binary)}`;
      };

      // Company details with fallback
      const companyName = companyProfile?.name || 'CloudNet Softwares';
      const companyPhone = companyProfile?.phone_number || '+66-948712350';
      const companyEmail = companyProfile?.email || 'info@cloudnetsoftwares.com';
      const companyWebsite = companyProfile?.website || 'www.cloudnetsoftwares.com';
      const companyAddress = companyProfile?.address || '123 Beach Road, Pattaya, Thailand';
      const companyCity = companyProfile?.city || '';
      const companyState = companyProfile?.state || '';
      const companyZip = companyProfile?.zip_code || '';
      const companyCountry = companyProfile?.country || '';
      const companyTaxId = companyProfile?.tax_id || '';
      const fullAddress = [companyAddress, companyCity, companyState, companyZip, companyCountry].filter(Boolean).join(', ');

      // Get logo if available
      const logoUrl = companyProfile?.logo && companyProfile?.logo_type
        ? blobToBase64(companyProfile.logo, companyProfile.logo_type)
        : null;

      // Get terms and conditions
      const termsAndConditions = companyProfile?.terms_and_conditions || `Terms & Conditions:
• All prices are inclusive of VAT
• Payment is due upon receipt
• Goods once sold cannot be returned`;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>VAT Invoice - ${invoiceData.invoice_number}</title>
          <style>
            @page { size: A4; margin: 8mm; }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 8px;
              background: white;
              color: #333;
            }
            .vat-invoice-container {
              max-width: 210mm;
              min-height: 270mm;
              margin: 0 auto;
              border: 2px solid #000;
              padding: 10px;
              display: flex;
              flex-direction: column;
            }
            .header-section {
              border-bottom: 3px solid #FF7F7F;
              padding-bottom: 6px;
              margin-bottom: 8px;
            }
            .company-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .company-logo-section {
              flex: 1;
            }
            .company-name {
              font-size: 13px;
              font-weight: bold;
              color: #050505ff;
              margin-bottom: 2px;
            }
            .company-tagline {
              font-size: 9px;
              color: #333;
              font-style: italic;
            }
            .company-contact {
              text-align: right;
              font-size: 14px;
              color: #0e0d0dff;
              line-height: 1.3;
            }
            .invoice-title {
              text-align: center;
              background: linear-gradient(135deg, #FF8A80 0%, #eb730a 100%);
              color: black;
              padding: 6px;
              margin: 6px 0;
              font-size: 16px;
              font-weight: bold;
              letter-spacing: 1px;
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
              margin: 6px 0;
            }
            .info-box {
              border: 1px solid #e0e0e0;
              padding: 6px;
              border-radius: 5px;
              background: #f8f9fa;
            }
            .info-box-title {
              font-weight: bold;
              font-size: 11px;
              color: #000;
              border-bottom: 2px solid #FF8A80;
              padding-bottom: 3px;
              margin-bottom: 4px;
            }
            .info-row {
              display: flex;
              padding: 2px 0;
              font-size: 9px;
              line-height: 1.2;
            }
            .info-label {
              font-weight: 600;
              width: 90px;
              color: #555;
            }
            .info-value {
              flex: 1;
              color: #333;
            }
            .items-wrapper {
              flex: 1;
              display: flex;
              flex-direction: column;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 6px 0;
              font-size: 9px;
              box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            }
            .items-table thead {
              background: linear-gradient(135deg, #b8b8b8ff 0%, #b8b8b8ff 100%);
              color: black;
            }
            .items-table th {
              padding: 5px 4px;
              text-align: left;
              font-weight: 600;
              border-right: 1px solid rgba(255,255,255,0.2);
            }
            .items-table th:last-child {
              border-right: none;
            }
            .items-table tbody tr {
              border-bottom: 1px solid #e0e0e0;
            }
            .items-table tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .items-table tbody tr:hover {
              background-color: #e3f2fd;
            }
            .items-table td {
              padding: 4px 4px;
              vertical-align: middle;
            }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .summary-section {
              margin-top: auto;
              display: flex;
              justify-content: flex-end;
            }
            .summary-box {
              width: 280px;
              border: 2px solid #000;
              border-radius: 5px;
              overflow: hidden;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 4px 10px;
              border-bottom: 1px solid #e0e0e0;
              font-size: 10px;
            }
            .summary-row:last-child {
              border-bottom: none;
            }
            .summary-label {
              font-weight: 600;
              color: #555;
            }
            .summary-value {
              font-weight: 500;
              color: #333;
            }
            .vat-row {
              background-color: #FFE5E5;
            }
            .total-row {
              background: linear-gradient(135deg, #FF8A80 0%, #eb730a 100%);
              color: white;
              font-size: 12px;
              font-weight: bold;
            }
            .footer-section {
              margin-top: 10px;
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              padding-top: 8px;
              border-top: 2px solid #FF8A80;
            }
            .terms-section {
              flex: 1;
              font-size: 8px;
              color: #666;
              line-height: 1.2;
            }
            .terms-title {
              font-weight: bold;
              margin-bottom: 3px;
              color: #FF8A80;
              font-size: 9px;
            }
            .signature-box {
              width: 160px;
              text-align: center;
            }
            .signature-line {
              border-top: 2px solid #000;
              margin-top: 30px;
              padding-top: 4px;
              font-weight: 600;
              color: #000;
              font-size: 9px;
            }
            .vat-badge {
              display: inline-block;
              background: #FF8A80;
              color: white;
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 9px;
              font-weight: bold;
              margin-left: 8px;
            }
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 120px;
              font-weight: bold;
              color: rgba(0, 0, 0, 0.08);
              z-index: 1;
              pointer-events: none;
              text-transform: uppercase;
              letter-spacing: 10px;
            }
            .vat-invoice-container {
              position: relative;
            }
            @media print {
              body { margin: 0; padding: 10mm; }
              .vat-invoice-container { border: none; }
              @page { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="vat-invoice-container">
            <div class="watermark">COPY</div>
            <div class="header-section">
              <div class="company-header">
                <div class="company-logo-section">
                  ${logoUrl ? `
                    <img src="${logoUrl}" alt="${companyName} Logo" style="max-width: 140px; max-height: 60px; margin-bottom: 6px;" onerror="this.style.display='none'" />
                  ` : ''}
                  <div class="company-name">${companyName}</div>
                  ${companyTaxId ? `<div class="company-tagline">Tax ID: ${companyTaxId}</div>` : ''}
                </div>
                <div class="company-contact">
                  <div>📞 ${companyPhone}</div>
                  <div>🌐 ${companyWebsite}</div>
                  <div>📧 ${companyEmail}</div>
                  <div style="margin-top: 8px;">📍 ${fullAddress}</div>
                </div>
              </div>
            </div>

            <div class="invoice-title">
              INVOICE

            </div>

            <div class="info-grid">
              <div class="info-box">
                <div class="info-box-title">📄 Invoice Information</div>
                <div class="info-row">
                  <span class="info-label">Invoice Number:</span>
                  <span class="info-value"><strong>${invoiceData.invoice_number}</strong></span>
                </div>
                <div class="info-row">
                  <span class="info-label">Invoice Date:</span>
                  <span class="info-value">${new Date(invoiceData.setup_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Invoice Time:</span>
                  <span class="info-value">${invoiceData.inv_time || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Payment Method:</span>
                  <span class="info-value"><strong>${invoiceData.payment_method || 'Cash'}</strong></span>
                </div>
              </div>

              <div class="info-box">
                <div class="info-box-title">👤 Customer Details</div>
                <div class="info-row">
                  <span class="info-label">Name:</span>
                  <span class="info-value"><strong>${invoiceData.customer_name}</strong></span>
                </div>
                <div class="info-row">
                  <span class="info-label">Phone:</span>
                  <span class="info-value">${invoiceData.customer_phone}</span>
                </div>
                ${invoiceData.customer_email ? `
                <div class="info-row">
                  <span class="info-label">Email:</span>
                  <span class="info-value">${invoiceData.customer_email}</span>
                </div>` : ''}
                ${invoiceData.customer_address ? `
                <div class="info-row">
                  <span class="info-label">Address:</span>
                  <span class="info-value">${invoiceData.customer_address}</span>
                </div>` : ''}
                ${invoiceData.customer_gst ? `
                <div class="info-row">
                  <span class="info-label">Tax ID:</span>
                  <span class="info-value">${invoiceData.customer_gst}</span>
                </div>` : ''}
              </div>
            </div>

            <div class="items-wrapper">
              <table class="items-table">
                <thead>
                  <tr>
                    <th width="5%" class="text-center">#</th>
                    <th width="${showVatDetails ? '30' : '35'}%">Item Description</th>
                    <th width="12%" class="text-right">Unit Price</th>
                    <th width="8%" class="text-center">Qty</th>
                    <th width="10%" class="text-right">Amount</th>
                    <th width="10%" class="text-right">Disc%</th>
                    ${showVatDetails ? '<th width="10%" class="text-right">VAT%</th>' : ''}
                    <th width="15%" class="text-right">Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoiceData.items && invoiceData.items.length > 0 ? invoiceData.items.map((item, index) => `
                    <tr>
                      <td class="text-center">${index + 1}</td>
                      <td><strong>${item.item_name || item.iname || 'N/A'}</strong>${item.description ? `<br><i style="font-size: 10px; color: #666;">${item.description}</i>` : ''}</td>
                      <td class="text-right">฿ ${(parseFloat(item.rate || item.offerprice || 0)).toFixed(2)}</td>
                      <td class="text-center">${item.quantity || 0}</td>
                      <td class="text-right">฿ ${(parseFloat(item.rate || item.offerprice || 0) * parseFloat(item.quantity || 1)).toFixed(2)}</td>
                      <td class="text-right">${parseFloat(item.discount_percent || 0).toFixed(0)}%</td>
                      ${showVatDetails ? `<td class="text-right">${parseFloat(item.vat || 7).toFixed(0)}%</td>` : ''}
                      <td class="text-right"><strong>฿ ${(parseFloat(item.total_amount || 0)).toFixed(2)}</strong></td>
                    </tr>
                  `).join('') : `<tr><td colspan="${showVatDetails ? '8' : '7'}" class="text-center">No items found</td></tr>`}
                </tbody>
              </table>
            </div>

            <hr style="border: none; border-top: 3px solid #000; margin: 15px 0;" />

            <div class="summary-section">
              <div class="summary-box">
                <div class="summary-row">
                  <span class="summary-label">Subtotal:</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Discount:</span>
                  <span class="summary-value">- ฿ ${parseFloat(invoiceData.discount_value || 0).toFixed(2)}</span>
                </div>
                ${showVatDetails ? `
                <div class="summary-row">
                  <span class="summary-label">Subtotal After Discount:</span>
                  <span class="summary-value">฿ ${(parseFloat(invoiceData.subtotal || 0) - parseFloat(invoiceData.discount_value || 0)).toFixed(2)}</span>
                </div>
                <div class="summary-row vat-row">
                  <span class="summary-label">🔖 VAT (7%):</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.tax || 0).toFixed(2)}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Round Off:</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.round_off || 0).toFixed(2)}</span>
                </div>
                ` : ''}
                <div class="summary-row total-row">
                  <span class="summary-label">GRAND TOTAL:</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.grand_total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div class="footer-section">
              <div class="terms-section">
                <div class="terms-title">Terms & Conditions:</div>
                ${termsAndConditions.split('\n').map(line => `<div>${line}</div>`).join('')}
                <div style="margin-top: 15px; font-size: 10px; font-style: italic;">
                  This is a computer-generated invoice and does not require a physical signature.
                </div>
              </div>
              ${(companyProfile?.bank_name || companyProfile?.account_number) ? `
              <div class="bank-details-box" style="width: 250px; border: 1px solid #e0e0e0; padding: 8px; border-radius: 5px; background: #f8f9fa; margin: 0 15px;">
                <div style="font-weight: bold; font-size: 10px; color: #000; border-bottom: 2px solid #FF8A80; padding-bottom: 3px; margin-bottom: 4px;">
                  🏦 Bank Details
                </div>
                ${companyProfile?.bank_name ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Bank Name:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.bank_name}</span>
                </div>` : ''}
                ${companyProfile?.account_name ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Account Name:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.account_name}</span>
                </div>` : ''}
                ${companyProfile?.account_number ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Account Number:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.account_number}</span>
                </div>` : ''}
                ${companyProfile?.routing_number ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Routing Number:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.routing_number}</span>
                </div>` : ''}
                ${companyProfile?.swift_code ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">SWIFT Code:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.swift_code}</span>
                </div>` : ''}
              </div>
              ` : ''}
              <div class="signature-box">
                <div class="signature-line">
                  Authorized Signatory
                </div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      toast.error('Error printing invoice');
      console.error('Print error:', error);
    }
  };

  const printVATInvoice = printBillVATFormat; // Legacy function name for compatibility

  const monthlyData = (() => {
    const summary = {};
    (filteredData.length > 0 ? filteredData : data).forEach((item) => {
      const month = format(parseISO(item.setup_date), "MMM yyyy");
      const total = parseFloat(item.grand_total || 0);
      summary[month] = (summary[month] || 0) + total;
    });
    return Object.entries(summary).map(([month, total]) => ({ month, total }));
  })();

  const totalSubtotal = filteredData.reduce((acc, item) => acc + parseFloat(item.subtotal_afterdiscount || 0), 0);
  const totalTax = filteredData.reduce((acc, item) => acc + parseFloat(item.tax || 0), 0);
  const totalGrand = filteredData.reduce((acc, item) => acc + parseFloat(item.grand_total || 0), 0);

  const tableColumns = [
    {
      title: "Inv. No.",
      dataIndex: "id",
      key: "id",
      align: "center",
      render: (id, row) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 700, color: "#1677ff" }}
          onClick={() => handleViewItems(row)}
          title="View bill details"
        >
          #{id}
        </Button>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer_id",
      key: "customer_id",
      align: "center",
      render: (customerId) => (
        <Tag color="geekblue" style={{ borderRadius: 12 }}>
          {customerNameById[customerId] || "Walk-in Customer"}
        </Tag>
      ),
    },
    {
      title: "Inv Date",
      dataIndex: "inv_date",
      key: "inv_date",
      align: "center",
      render: (v) => <Text type="secondary">{v || "-"}</Text>,
    },
    {
      title: "Setup Date",
      dataIndex: "setup_date",
      key: "setup_date",
      align: "center",
      render: (v) => <Text type="secondary">{v || "-"}</Text>,
    },
    {
      title: "Time",
      dataIndex: "inv_time",
      key: "inv_time",
      align: "center",
      render: (v) => <Text type="secondary">{v || "-"}</Text>,
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal_afterdiscount",
      key: "subtotal_afterdiscount",
      align: "right",
      render: (v) => <Text style={{ color: "#1677ff" }}>{`฿ ${parseFloat(v || 0).toFixed(2)}`}</Text>,
    },
    {
      title: "Tax",
      dataIndex: "tax",
      key: "tax",
      align: "right",
      render: (v) => <Text style={{ color: "#fa8c16" }}>{`฿ ${parseFloat(v || 0).toFixed(2)}`}</Text>,
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right",
      render: (v) => <Text strong style={{ color: "#2e7d32" }}>{`฿ ${parseFloat(v || 0).toFixed(2)}`}</Text>,
    },
    {
      title: "Payment",
      dataIndex: "payment_mode",
      key: "payment_mode",
      align: "center",
      render: (mode) => <Tag color={PAYMENT_TAG_COLOR[mode] || "default"}>{mode || "N/A"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, row) => (
        <Space>
          <Button size="small" type="primary" icon={<PrinterOutlined />} onClick={() => printVATInvoice(row)} title="Print A4 VAT Format">
            Print
          </Button>
          {row.status !== 2 && row.status !== "2" && (
            <Popconfirm
              title="Cancel this bill?"
              description="This will mark the bill as cancelled and remove its ledger entries."
              okText="Yes, Cancel"
              okType="danger"
              cancelText="No"
              onConfirm={() => handleCancelBill(row)}
            >
              <Button size="small" danger icon={<StopOutlined />} title="Cancel Bill">
                Cancel
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Navigation functions for cashiers
  const navigateToDashboard = () => {
    navigate('/dashboard');
  };

  const navigateBack = () => {
    navigate(-1);
  };

  // Check if mobile device
  const isMobile = window.innerWidth <= 768;

  // Render content without Layout/Header for cashiers
  const renderContent = () => (
    <>
      <ToastContainer />
      <style>{`
        .bh-row-even td { background-color: #fafcff; }
        .bh-row-odd td { background-color: #ffffff; }
      `}</style>

      {/* Cashier Navigation Header */}
      {isCashier && (
        <div style={{
          backgroundColor: '#1f1f1f',
          color: 'white',
          padding: isMobile ? '10px 15px' : '14px 20px',
          marginBottom: '20px'
        }}>
          <Row justify="space-between" align="middle" gutter={[8, 8]}>
            <Col>
              <Space size={12}>
                <Button ghost icon={<ArrowLeftOutlined />} onClick={navigateBack} size={isMobile ? "small" : "middle"}>
                  Back
                </Button>
                <Title level={isMobile ? 5 : 4} style={{ color: 'white', margin: 0 }}>
                  Sales Reports
                </Title>
              </Space>
            </Col>
            <Col>
              <Button ghost icon={<HomeOutlined />} onClick={navigateToDashboard} size={isMobile ? "small" : "middle"}>
                Dashboard
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <div style={{ padding: isCashier ? '0 20px 20px' : 0 }}>
        <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "active",
                label: (
                  <span><CheckCircleOutlined /> Active Bills</span>
                ),
              },
              {
                key: "cancelled",
                label: (
                  <span><CloseCircleOutlined /> Cancelled Bills</span>
                ),
              },
            ]}
          />

          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>Date Range</Text>
              <RangePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                allowEmpty={[true, true]}
                value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
                onChange={(_, dateStrings) => {
                  setStartDate(dateStrings?.[0] || "");
                  setEndDate(dateStrings?.[1] || "");
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Payment Mode</Text>
              <Select
                style={{ width: "100%" }}
                value={paymentMode || undefined}
                placeholder="All"
                allowClear
                onChange={(v) => setPaymentMode(v || "")}
                options={[
                  { value: "Cash", label: "Cash" },
                  { value: "Credit", label: "Credit" },
                  { value: "Bank Transfer", label: "Bank" },
                  { value: "Entertainment", label: "Entertainment" },
                  { value: "UPI", label: "UPI" },
                  { value: "QR Code", label: "QR Code" },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Invoice No.</Text>
              <Input
                value={orderNoSearch}
                onChange={(e) => setOrderNoSearch(e.target.value)}
                placeholder="Search..."
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Table</Text>
              <Input
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                placeholder="Table no."
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sort By</Text>
              <Select
                style={{ width: "100%" }}
                value={sortBy}
                onChange={setSortBy}
                options={[
                  { value: "date", label: "Date & Time" },
                  { value: "amount", label: "Amount" },
                  { value: "table", label: "Table" },
                  { value: "invoice", label: "Invoice" },
                ]}
              />
            </Col>
          </Row>

          <Row gutter={[12, 12]} style={{ marginTop: 12 }} align="bottom">
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Min Amount</Text>
              <InputNumber
                style={{ width: "100%" }}
                value={amountFilter.min}
                onChange={(v) => setAmountFilter(prev => ({ ...prev, min: v ?? "" }))}
                placeholder="Min ฿"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Max Amount</Text>
              <InputNumber
                style={{ width: "100%" }}
                value={amountFilter.max}
                onChange={(v) => setAmountFilter(prev => ({ ...prev, max: v ?? "" }))}
                placeholder="Max ฿"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sort Order</Text>
              <Select
                style={{ width: "100%" }}
                value={sortOrder}
                onChange={setSortOrder}
                options={[
                  { value: "desc", label: "Newest First" },
                  { value: "asc", label: "Oldest First" },
                ]}
              />
            </Col>
            <Col xs={24} md={12}>
              <Space wrap>
                <Button icon={<ReloadOutlined />} onClick={clearFilters}>Clear</Button>
                <Button icon={<FileExcelOutlined />} onClick={exportCSV}>CSV</Button>
                <Button danger icon={<FilePdfOutlined />} onClick={exportPDF}>PDF</Button>
                <Tag color="blue" style={{ padding: "4px 10px", fontSize: 13 }}>
                  {filteredData.length} / {data.length} Bills
                </Tag>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card bordered={false} style={{ borderRadius: 12 }}>
          <Table
            rowKey="id"
            columns={tableColumns}
            dataSource={filteredData}
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} bills` }}
            scroll={{ x: true }}
            rowClassName={(_, index) => (index % 2 === 0 ? "bh-row-even" : "bh-row-odd")}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5} align="right">
                    <Text strong>{activeTab === "cancelled" ? "Cancelled Total" : "Total"}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong style={{ color: "#1677ff" }}>฿ {totalSubtotal.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <Text strong style={{ color: "#fa8c16" }}>฿ {totalTax.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <Text strong style={{ color: "#2e7d32" }}>฿ {totalGrand.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} colSpan={2} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>

        <Card bordered={false} style={{ borderRadius: 12, marginTop: 16 }}>
          <Title level={5} style={{ marginBottom: 16 }}>Monthly Sales Summary</Title>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total" fill="#1677ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {showModal && (
        <BillItemModal
          isOpen={showModal}
          bill={selectedBill}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );

  // Main return statement with conditional rendering
  return isCashier ? (
    <div style={{
      width: '100vw',
      height: '100vh',
      backgroundColor: '#f5f5f5',
      overflow: 'auto'
    }}>
      {renderContent()}
    </div>
  ) : (
    <Layout>
      <Header title="Sale Report" />
      <div style={{ padding: 20 }}>
        {renderContent()}
      </div>
    </Layout>
  );
}
